import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Search, Eye, Trash2, MoreVertical, 
  Users, Calendar, MapPin, DollarSign
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const statusConfig = {
  planning: { label: 'Planejando', color: 'bg-yellow-500/20 text-yellow-400' },
  active: { label: 'Ativa', color: 'bg-green-500/20 text-green-400' },
  in_progress: { label: 'Em andamento', color: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Concluída', color: 'bg-slate-500/20 text-slate-400' },
  cancelled: { label: 'Cancelada', color: 'bg-red-500/20 text-red-400' },
  voting: { label: 'Votação', color: 'bg-purple-500/20 text-purple-400' },
};

export default function AdminTrips() {
  const { api } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tripDetails, setTripDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);

  const limit = 20;

  useEffect(() => {
    fetchTrips();
  }, [page, search]);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/trips', {
        params: { skip: page * limit, limit, search: search || undefined }
      });
      setTrips(response.data.trips);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Erro ao carregar viagens');
    } finally {
      setLoading(false);
    }
  };

  const fetchTripDetails = async (tripId) => {
    try {
      setLoadingDetails(true);
      const response = await api.get(`/admin/trips/${tripId}`);
      setTripDetails(response.data);
    } catch (error) {
      console.error('Error fetching trip details:', error);
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDelete = async () => {
    if (!tripToDelete) return;
    
    try {
      await api.delete(`/admin/trips/${tripToDelete.id}`);
      toast.success('Viagem excluída com sucesso');
      setDeleteOpen(false);
      setTripToDelete(null);
      fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir viagem');
    }
  };

  const openDetails = (trip) => {
    setSelectedTrip(trip);
    setDetailsOpen(true);
    fetchTripDetails(trip.id);
  };

  const openDelete = (trip) => {
    setTripToDelete(trip);
    setDeleteOpen(true);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Viagens</h1>
          <p className="text-slate-400">Gerencie todas as viagens da plataforma</p>
        </div>
        <Badge className="bg-green-500/20 text-green-400">
          {total} viagens
        </Badge>
      </div>

      {/* Search */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou destino..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
            />
          </div>
        </CardContent>
      </Card>

      {/* Trips Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Viagem</TableHead>
                  <TableHead className="text-slate-400">Organizador</TableHead>
                  <TableHead className="text-slate-400">Membros</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Data Início</TableHead>
                  <TableHead className="text-slate-400">Criada em</TableHead>
                  <TableHead className="text-slate-400 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => {
                  const status = statusConfig[trip.status] || statusConfig.planning;
                  return (
                    <TableRow key={trip.id} className="border-slate-700">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{trip.name}</p>
                          <p className="text-sm text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {trip.destination || 'Sem destino'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {trip.organizer ? (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-300">{trip.organizer.name}</span>
                          </div>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-300">
                          <Users className="w-4 h-4" />
                          {trip.members_count || 0}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={status.color}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {trip.start_date 
                          ? format(parseISO(trip.start_date), "dd/MM/yyyy", { locale: ptBR })
                          : '-'
                        }
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {trip.created_at 
                          ? format(parseISO(trip.created_at), "dd/MM/yyyy", { locale: ptBR })
                          : '-'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem 
                              className="text-slate-300 focus:text-white focus:bg-slate-700"
                              onClick={() => openDetails(trip)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-400 focus:text-red-300 focus:bg-slate-700"
                              onClick={() => openDelete(trip)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-slate-700 text-slate-300"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="border-slate-700 text-slate-300"
            >
              Próximo
            </Button>
          </div>
        </div>
      )}

      {/* Trip Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <div>
                <p className="font-bold text-xl">{selectedTrip?.name}</p>
                <p className="text-sm text-slate-400 font-normal flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {selectedTrip?.destination || 'Sem destino'}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : tripDetails && (
            <div className="space-y-6 mt-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{tripDetails.members?.length || 0}</p>
                  <p className="text-xs text-slate-400">Membros</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Calendar className="w-5 h-5 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{tripDetails.itinerary?.length || 0}</p>
                  <p className="text-xs text-slate-400">Atividades</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <DollarSign className="w-5 h-5 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{tripDetails.payments?.length || 0}</p>
                  <p className="text-xs text-slate-400">Pagamentos</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <DollarSign className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-lg font-bold">
                    R$ {(tripDetails.payments?.reduce((acc, p) => acc + p.total_amount, 0) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-400">Total</p>
                </div>
              </div>

              {/* Organizer */}
              {tripDetails.organizer && (
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <h3 className="font-semibold mb-2 text-slate-300">Organizador</h3>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 bg-slate-700">
                      <AvatarFallback>👑</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-white">{tripDetails.organizer.name}</p>
                      <p className="text-sm text-slate-400">{tripDetails.organizer.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Members List */}
              {tripDetails.members?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-slate-300">Membros ({tripDetails.members.length})</h3>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {tripDetails.members.map((member) => (
                      <div key={member.id} className="flex items-center gap-2 bg-slate-700/30 rounded-lg p-2">
                        <Avatar className="w-6 h-6 bg-slate-700">
                          <AvatarFallback className="text-sm bg-transparent">
                            {member.user?.avatar_emoji || '😊'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-slate-300 truncate">{member.user?.name || 'Usuário'}</span>
                        <Badge className={
                          member.status === 'confirmed' ? 'bg-green-500/20 text-green-400 text-xs' :
                          'bg-yellow-500/20 text-yellow-400 text-xs'
                        }>
                          {member.role === 'organizer' ? 'Org' : member.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir viagem?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta ação não pode ser desfeita. Todos os dados da viagem "{tripToDelete?.name}" 
              serão excluídos permanentemente, incluindo membros, pagamentos, roteiro e arquivos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
